import { useEffect, useState } from "react";
import { X, Sparkle, Trophy, Warning, Target } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../../lib/teleproApi";

/**
 * EndOfWeekRecapModal — Vague 11-E.
 *
 * Triggered by the télépro's "🗓 Terminer ma semaine" button — NOT a
 * calendar cron, because some télépros work Saturdays. IA analyses the
 * ISO-week (Monday 00:00 → now) and returns highlights, watch_outs,
 * focus_next, morale — plus a per-day breakdown chart.
 */
export default function EndOfWeekRecapModal({ open, onClose }) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState(false);

  const trigger = async () => {
    setState({ loading: true, data: null, error: null });
    setEmailed(false);
    try {
      const r = await teleproApi.post("/ai/end-of-week");
      if (r?.data) setState({ loading: false, data: r.data, error: null });
      else setState({ loading: false, data: null, error: "L'IA n'a rien retourné" });
    } catch (e) {
      setState({ loading: false, data: null, error: e.message });
    }
  };

  const sendByEmail = async () => {
    if (emailing) return;
    setEmailing(true);
    try {
      const r = await teleproApi.post("/me/reports/end-of-week");
      const d = r?.data?.delivery || {};
      if (d.sent_to_telepro || d.sent_to_admin) {
        setEmailed(true);
        const bits = [];
        if (d.sent_to_telepro) bits.push("ton email");
        if (d.sent_to_admin) bits.push("l'admin");
        toast.success(`📧 Rapport envoyé sur ${bits.join(" + ")}`);
      } else {
        toast.error("Envoi email impossible pour le moment");
      }
    } catch (e) {
      toast.error("Erreur envoi email : " + (e?.message || "réessaie plus tard"));
    } finally {
      setEmailing(false);
    }
  };

  useEffect(() => {
    if (open && !state.data && !state.loading && !state.error) trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const d = state.data;
  const daily = d?.daily_breakdown || [];
  const maxCalls = Math.max(1, ...daily.map((x) => x.calls));

  const dayLabel = (iso) => {
    if (!iso) return "?";
    const dt = new Date(iso);
    const short = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"][dt.getDay()];
    return `${short} ${dt.getDate()}`;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}/>
      <div
        className="fixed inset-x-3 top-6 bottom-6 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[580px] sm:max-w-[92vw] sm:max-h-[85vh] z-50 bg-card border border-primary/30 rounded-sm shadow-xl overflow-y-auto"
        data-testid="end-of-week-modal"
      >
        <div className="sticky top-0 bg-gradient-to-r from-secondary to-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Trophy size={20} weight="fill" className="text-yellow-300 shrink-0"/>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider opacity-80">Bilan hebdo IA</div>
              <div className="font-display font-medium text-sm truncate">Ma semaine — {d?.week_start || "en cours"}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-primary-foreground/80 hover:text-white p-1"
            data-testid="end-of-week-close"
          >
            <X size={16} weight="bold"/>
          </button>
        </div>

        {state.loading && !d && (
          <div className="p-8 text-center">
            <Sparkle size={32} weight="fill" className="inline text-yellow-500 animate-pulse"/>
            <div className="mt-3 text-sm text-muted-foreground italic">
              L'IA compile tes 7 derniers jours…
            </div>
          </div>
        )}

        {state.error && (
          <div className="p-6 text-center">
            <div className="text-sm text-red-800 mb-3">❌ {state.error}</div>
            <button onClick={trigger} className="text-xs font-semibold bg-primary text-primary-foreground rounded-sm px-3 py-1.5" data-testid="end-of-week-retry">Réessayer</button>
          </div>
        )}

        {d && (
          <div className="p-4 space-y-4" data-testid="end-of-week-result">
            {/* Week KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: "Appels", val: d.week_stats?.total_calls || 0 },
                { label: "Jours travaillés", val: d.week_stats?.days_worked || 0 },
                { label: "Opportunités", val: d.week_stats?.opportunities || 0, accent: true },
                { label: "Ventes", val: d.week_stats?.sales || 0, accent: true },
              ].map((s) => (
                <div key={s.label} className={`text-center rounded-sm px-2 py-2 border ${s.accent ? "bg-primary text-primary-foreground border-primary" : "bg-muted/30 border-border"}`}>
                  <div className={`text-[9px] uppercase tracking-wider font-bold ${s.accent ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{s.label}</div>
                  <div className="font-display text-xl font-medium tabular-nums leading-none mt-0.5">{s.val}</div>
                </div>
              ))}
            </div>

            {/* Daily bar chart */}
            {daily.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
                  Répartition jour par jour
                </div>
                <div className="flex items-end justify-between gap-1 h-24 bg-muted/20 border border-border rounded-sm p-2">
                  {daily.map((d0) => (
                    <div key={d0.date} className="flex-1 flex flex-col items-center gap-0.5" data-testid={`week-day-${d0.date}`}>
                      <div className="w-full flex flex-col justify-end h-full">
                        <div
                          className="w-full bg-primary/60 rounded-t-sm relative group cursor-help"
                          style={{ height: `${Math.max(4, (d0.calls / maxCalls) * 100)}%` }}
                          title={`${d0.calls} appels · ${d0.opps} opps · ${d0.sales} ventes`}
                        >
                          {d0.sales > 0 && (
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px]">🎉</div>
                          )}
                        </div>
                      </div>
                      <div className="text-[9px] text-muted-foreground text-center">{dayLabel(d0.date)}</div>
                      <div className="text-[10px] font-bold tabular-nums">{d0.calls}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Week vs previous week — Vague 11-F */}
            {d.week_over_week && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
                  Cette semaine vs semaine dernière
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { key: "total_calls", label: "Appels" },
                    { key: "opportunities", label: "Opps" },
                    { key: "sales", label: "Ventes" },
                    { key: "avg_calls_per_day", label: "Moy./jour" },
                  ].map(({ key, label }) => {
                    const w = d.week_over_week[key] || {};
                    const cur = w.current ?? 0;
                    const prev = w.previous ?? 0;
                    const delta = w.delta ?? 0;
                    const dpct = w.delta_pct;
                    const positive = delta > 0;
                    const negative = delta < 0;
                    return (
                      <div key={key} className="border border-border rounded-sm p-2" data-testid={`wow-${key}`}>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-0.5">{label}</div>
                        <div className="flex items-baseline gap-1.5">
                          <div className="font-display text-lg tabular-nums font-medium">{cur}</div>
                          <div className={`text-[10px] font-bold tabular-nums ${positive ? "text-green-700" : negative ? "text-red-700" : "text-muted-foreground"}`}>
                            {positive ? "▲" : negative ? "▼" : "="}
                            {dpct !== null ? ` ${Math.abs(dpct)}%` : ""}
                          </div>
                        </div>
                        <div className="text-[9px] text-muted-foreground tabular-nums">
                          semaine dernière : <b>{prev}</b>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Twin daily bars — current vs previous */}
                {(daily.length > 0 || (d.previous_daily_breakdown || []).length > 0) && (
                  <div className="mt-2 bg-muted/20 border border-border rounded-sm p-2">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5 text-center">
                      Volume d'appels — cette semaine (foncé) vs précédente (clair)
                    </div>
                    <div className="flex items-end justify-between gap-1 h-16">
                      {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((lab, i) => {
                        const curD = daily.find((x) => new Date(x.date).getDay() === (i + 1) % 7);
                        const prvD = (d.previous_daily_breakdown || []).find((x) => new Date(x.date).getDay() === (i + 1) % 7);
                        const localMax = Math.max(1, ...daily.map((x) => x.calls), ...(d.previous_daily_breakdown || []).map((x) => x.calls));
                        return (
                          <div key={lab} className="flex-1 flex flex-col items-center gap-0.5">
                            <div className="w-full flex justify-center items-end gap-0.5 h-full">
                              <div
                                className="w-1/2 bg-primary rounded-t-sm"
                                style={{ height: `${Math.max(2, ((curD?.calls || 0) / localMax) * 100)}%` }}
                                title={`${lab} cette semaine : ${curD?.calls || 0} appels`}
                              />
                              <div
                                className="w-1/2 bg-primary/30 rounded-t-sm"
                                style={{ height: `${Math.max(2, ((prvD?.calls || 0) / localMax) * 100)}%` }}
                                title={`${lab} semaine dernière : ${prvD?.calls || 0} appels`}
                              />
                            </div>
                            <div className="text-[9px] text-muted-foreground">{lab}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Highlights */}
            {d.highlights?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-2 flex items-center gap-1">
                  <Trophy size={11} weight="fill" className="text-yellow-500"/> Points forts
                </div>
                <ul className="space-y-1.5">
                  {d.highlights.map((h, i) => (
                    <li key={i} className="text-sm flex items-start gap-1.5 bg-primary/5 border-l-2 border-primary/40 pl-2 py-1 rounded-sm">
                      <span className="text-primary font-bold shrink-0">•</span>
                      <span className="leading-snug">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Watch-outs */}
            {d.watch_outs?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-orange-800 mb-2 flex items-center gap-1">
                  <Warning size={11} weight="fill" className="text-orange-500"/> À corriger
                </div>
                <ul className="space-y-1.5">
                  {d.watch_outs.map((w, i) => (
                    <li key={i} className="text-sm flex items-start gap-1.5 bg-orange-500/5 border-l-2 border-orange-500/40 pl-2 py-1 rounded-sm">
                      <span className="text-orange-800 font-bold shrink-0">•</span>
                      <span className="leading-snug">{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Focus next week */}
            {d.focus_next && (
              <div className="bg-secondary/10 border border-secondary/40 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-secondary mb-1 flex items-center gap-1">
                  <Target size={11} weight="fill"/> Focus pour la semaine prochaine
                </div>
                <div className="text-sm leading-snug font-medium">{d.focus_next}</div>
              </div>
            )}

            {/* Morale */}
            {d.morale && (
              <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-sm p-3 text-center">
                <div className="text-sm font-medium italic">"{d.morale}"</div>
              </div>
            )}

            <button
              onClick={sendByEmail}
              disabled={emailing || emailed}
              className={`w-full rounded-sm py-2.5 text-sm font-bold border ${emailed ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-primary/40 text-primary hover:bg-primary/5"} ${emailing ? "opacity-60 cursor-wait" : ""}`}
              data-testid="end-of-week-email"
            >
              {emailing ? "Envoi en cours…" : emailed ? "✅ Envoyé (toi + admin)" : "📧 M'envoyer le rapport par email"}
            </button>

            <button
              onClick={() => { toast.success("Bon week-end ! 🎉"); onClose?.(); }}
              className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] rounded-sm py-2.5 text-sm font-bold"
              data-testid="end-of-week-finish"
            >
              🎉 Fermer et profiter du week-end
            </button>
          </div>
        )}
      </div>
    </>
  );
}
